// Copied from Acend
import { InputProps } from "@chakra-ui/react";

import { enumSpecialCases } from "./enumSpecialCases";

/* eslint-disable @typescript-eslint/no-explicit-any */
const hasKey = <T extends Record<any, any>>(
  key: PropertyKey,
  obj: T
): key is keyof T => key in obj;

const formatEnumSection = (str: string) => {
  const upper = str.toUpperCase();
  if (upper in enumSpecialCases) {
    return enumSpecialCases[upper as keyof typeof enumSpecialCases];
  }
  return upper[0] + upper.slice(1).toLowerCase();
};

const toCapitalCase = (input: string): string => {
  // Check if the input is in all caps
  if (input === input.toUpperCase()) {
    return input
      .split("_")
      .map(word => formatEnumSection(word.trim()))
      .join(" ");
  }

  // Check if the input is in camelCase
  if (input !== input.toLowerCase()) {
    return input
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .split(" ")
      .map(word => formatEnumSection(word.trim()))
      .join(" ");
  }

  // Assume separate words, capitalize each word
  return input
    .split(" ")
    .map(word => formatEnumSection(word.trim()))
    .join(" ");
};

/** Object with display text as key and const as value to create options from */
export type Options = Record<string, string>;

/**
 * Overrides for the display text for specific options
 * @example SelectOptionOverrides<EquityType> = { "PreferredAdr": "Preferred ADR" }
 */
export type SelectOptionOverrides<T extends Options> = Partial<
  Record<keyof T, string>
>;

/**
 * Conditionally removes the display for specific options
 * @example SelectOptionConditionallyDisplay = { "TradeRebookings": boolean }
 */
export type SelectOptionConditionallyDisplay<T extends Options> = Partial<
  Record<keyof T, boolean>
>;

/** Config object for altering how options are created */
export type SelectOptionsConfig<T extends Options> = {
  /** Format choice when transforming option keys (display values):
   *
   * `"CAPITAL_CASE"` (default): transform keys to "Capital Case"
   *
   * `"UPPERCASE"`: transform keys to uppercase
   *
   * `"NONE"`: leave keys as given
   */
  keyFormatting?: "CAPITAL_CASE" | "UPPERCASE" | "NONE";
  /**
   * Object with display text overrides for specific options. Overrides will not be run though the key formatter and used as is.
   */
  overrides?: SelectOptionOverrides<T>;
  /**
   * How options with an `*_UNSPECIFIED` or `UNDEFINED` value should be handled:
   *
   * `"REMOVE"`: removed as an option
   *
   * `"EMPTY_DISPLAY"` (default): displayed as an empty string
   *
   * `"KEEP"`: treat like any other option
   *
   * When using `"KEEP"`, the same `keyFormatting` will be applied to the unspecified field as every other key. When using `"EMPTY_DISPLAY"`, the empty value will be the first option (regardless of where it is in the original object order)
   */
  unspecified?: "EMPTY_DISPLAY" | "KEEP" | "REMOVE";
  /**
   Takes the formatted value and passes to function where new formatted value is returned.
   */
  displayFormatter?: (val: string) => string;
  /**
   * Object with a boolean for specific options. Will not display it if the boolean is false.
   */
  conditionallyDisplay?: SelectOptionConditionallyDisplay<T>;
  /**
   Array of options to be removed from the list
   */
  removeOptions?: T[keyof T][];
};

/** Options and config for a (multi)select field */
export type SelectOptions<T extends Options> = {
  /** Object with display text as key and const as value to create options from */
  options: T;
  /** Config object for altering how options are created */
  optionsConfig?: SelectOptionsConfig<T>;
};

export interface MultiSelectProps<T extends Options>
  extends Omit<InputProps, "onChange"> {
  name: string;
  options: Options;
  setValue: (value: string[]) => void;
  value: string[];
  optionsConfig?: SelectOptionsConfig<T>;
  labelHeight?: number;
  onChange?: (value: string[]) => void;
  testId?: string;
  collapseInput?: boolean;
  maxLength?: number;
  allowCustomInput?: boolean;
}
/** Options post key formatting */
type FormattedOptions<T> = [string, T[keyof T]][];

/**
 * Creates an array of [display, value] tuples from a given object. By default, it will replace the `*_UNSPECIFIED` or `UNDEFINED` with an empty string display value (`{ "": *_UNSPECIFIED }`) and make the first option.
 */
export const formatOptions = <T extends Options>(
  options: T,
  {
    keyFormatting = "CAPITAL_CASE",
    overrides,
    unspecified = "EMPTY_DISPLAY",
    conditionallyDisplay,
    displayFormatter,
    removeOptions = [],
  }: SelectOptionsConfig<T>
): FormattedOptions<T> => {
  let defaultOption: [string, T[keyof T]] | undefined;
  const optionTuples = Object.entries(options) as FormattedOptions<T>;

  const formattedTuples = optionTuples.reduce<FormattedOptions<T>>(
    (optionTuples, [display, value]) => {
      if (removeOptions.includes(value)) {
        return optionTuples;
      }

      if (conditionallyDisplay && hasKey(display, conditionallyDisplay)) {
        if (!conditionallyDisplay[display]) {
          return optionTuples;
        }
      }
      if (overrides && hasKey(display, overrides)) {
        if (
          unspecified === "KEEP" &&
          (value.includes("UNSPECIFIED") ||
            value.includes("UNDEFINED") ||
            value.includes("UNKNOWN"))
        ) {
          return [[overrides[display] as string, value], ...optionTuples];
        }
        return [...optionTuples, [overrides[display] as string, value]];
      }

      if (
        unspecified !== "KEEP" &&
        (value.includes("UNSPECIFIED") ||
          value.includes("UNDEFINED") ||
          value.includes("UNKNOWN"))
      ) {
        if (unspecified === "EMPTY_DISPLAY") {
          defaultOption = ["", value];
        }
        // skip when unspecified === "REMOVE"
        return optionTuples;
      }

      let formattedDisplay: string;

      if (keyFormatting === "CAPITAL_CASE") {
        formattedDisplay = toCapitalCase(display);
      } else if (keyFormatting === "UPPERCASE") {
        formattedDisplay = display.toLocaleUpperCase();
      } else {
        formattedDisplay = display;
      }

      if (displayFormatter) {
        formattedDisplay = displayFormatter(formattedDisplay);
      }

      return [...optionTuples, [formattedDisplay, value]];
    },
    []
  );
  const orderedTuples = overrides
    ? formattedTuples.sort((a, b) => a[0].localeCompare(b[0]))
    : formattedTuples;

  return defaultOption ? [defaultOption, ...orderedTuples] : orderedTuples;
};

export default formatOptions;
